'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import * as SubframeUtils from '../../utils';
import { Checkbox } from '../Checkbox';
import { Table } from '../Table';
import { DataTableEmptyStateRow, DataTableErrorRow } from './DataTableEmptyState';
import { DataTableRowActions } from './DataTableRowActions';
import { DataTableSkeleton } from './DataTableSkeleton';
import type { ColumnDef, DataTableEmptyStateConfig, DataTableErrorStateConfig, RowAction } from './types';
interface DataTableBodyProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  getRowId: (row: T) => string;
  selectionMode?: 'none' | 'multiple';
  selectedIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  isLoading?: boolean;
  skeletonRowCount?: number;
  error?: DataTableErrorStateConfig | null;
  emptyState?: DataTableEmptyStateConfig;
  emptyFilterState?: DataTableEmptyStateConfig;
  hasActiveFilters?: boolean;
  onRowClick?: (row: T) => void;
  rowClassName?: string | ((row: T) => string);
  rowActions?: RowAction<T>[];
  getRowActionLabel?: (row: T) => string;
  initiallyOpenActionRowId?: string;
  cellPaddingX?: string;
}
export function DataTableBody(t0) {
  const $ = _c(47);
  const {
    data,
    columns,
    getRowId,
    selectionMode: t1,
    selectedIds,
    onSelectionChange,
    isLoading: t2,
    skeletonRowCount: t3,
    error,
    emptyState,
    emptyFilterState,
    hasActiveFilters: t4,
    onRowClick,
    rowClassName,
    rowActions,
    getRowActionLabel,
    initiallyOpenActionRowId,
    cellPaddingX: t5
  } = t0;
  const selectionMode = t1 === undefined ? "none" : t1;
  const isLoading = t2 === undefined ? false : t2;
  const skeletonRowCount = t3 === undefined ? 5 : t3;
  const hasActiveFilters = t4 === undefined ? false : t4;
  const cellPaddingX = t5 === undefined ? "px-6" : t5;
  const hasRowActions = !!rowActions && rowActions.length > 0;
  const totalColSpan = columns.length + (selectionMode === "multiple" ? 1 : 0) + (hasRowActions ? 1 : 0);
  let t6;
  if ($[0] !== onSelectionChange || $[1] !== selectedIds) {
    t6 = id => {
      if (!selectedIds || !onSelectionChange) {
        return;
      }
      const next = new Set(selectedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onSelectionChange(next);
    };
    $[0] = onSelectionChange;
    $[1] = selectedIds;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const toggleSelect = t6;
  if (isLoading) {
    let t7;
    if ($[3] !== cellPaddingX || $[4] !== columns || $[5] !== hasRowActions || $[6] !== selectionMode || $[7] !== skeletonRowCount) {
      t7 = <DataTableSkeleton columns={columns} rowCount={skeletonRowCount} selectionMode={selectionMode} hasRowActions={hasRowActions} cellPaddingX={cellPaddingX} />;
      $[3] = cellPaddingX;
      $[4] = columns;
      $[5] = hasRowActions;
      $[6] = selectionMode;
      $[7] = skeletonRowCount;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    return t7;
  }
  if (error) {
    let t7;
    if ($[9] !== error || $[10] !== totalColSpan) {
      t7 = <DataTableErrorRow colSpan={totalColSpan} config={error} />;
      $[9] = error;
      $[10] = totalColSpan;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  if (data.length === 0) {
    if (hasActiveFilters && emptyFilterState) {
      let t7;
      if ($[12] !== emptyFilterState || $[13] !== totalColSpan) {
        t7 = <DataTableEmptyStateRow colSpan={totalColSpan} config={emptyFilterState} />;
        $[12] = emptyFilterState;
        $[13] = totalColSpan;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      return t7;
    }
    if (emptyState) {
      let t7;
      if ($[15] !== emptyState || $[16] !== totalColSpan) {
        t7 = <DataTableEmptyStateRow colSpan={totalColSpan} config={emptyState} />;
        $[15] = emptyState;
        $[16] = totalColSpan;
        $[17] = t7;
      } else {
        t7 = $[17];
      }
      return t7;
    }
    return null;
  }
  let t7;
  if ($[18] !== cellPaddingX || $[19] !== columns || $[20] !== data || $[21] !== getRowActionLabel || $[22] !== getRowId || $[23] !== hasRowActions || $[24] !== initiallyOpenActionRowId || $[25] !== onRowClick || $[26] !== rowActions || $[27] !== rowClassName || $[28] !== selectedIds || $[29] !== selectionMode || $[30] !== toggleSelect) {
    let t8;
    if ($[32] !== cellPaddingX || $[33] !== columns || $[34] !== getRowActionLabel || $[35] !== getRowId || $[36] !== hasRowActions || $[37] !== initiallyOpenActionRowId || $[38] !== onRowClick || $[39] !== rowActions || $[40] !== rowClassName || $[41] !== selectedIds || $[42] !== selectionMode || $[43] !== toggleSelect) {
      t8 = (row, rowIndex) => {
        const rowId = getRowId(row);
        const isSelected = selectedIds?.has(rowId) ?? false;
        const resolvedRowClassName = typeof rowClassName === "function" ? rowClassName(row) : rowClassName ?? "";
        return <Table.Row key={rowId} data-testid={`datatable-row-${rowId}`} className={SubframeUtils.twClassNames("min-h-[67px] transition-colors", onRowClick && "cursor-pointer hover:bg-neutral-50", isSelected && "bg-neutral-50", resolvedRowClassName)} onClick={() => onRowClick?.(row)}>{selectionMode === "multiple" && <Table.Cell className={SubframeUtils.twClassNames("min-h-[67px] justify-center py-3", cellPaddingX)}><Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(rowId)} onClick={_temp} /></Table.Cell>}{columns.map(col => {
            const alignClass = col.align === "right" ? "justify-end" : col.align === "center" ? "justify-center" : "";
            let content;
            if (col.cell) {
              content = col.cell(row, rowIndex);
            } else {
              if (col.accessorKey) {
                const value = row[col.accessorKey];
                content = <span className="text-body font-body text-default-font">{value != null ? String(value) : "-"}</span>;
              } else {
                content = "-";
              }
            }
            return <Table.Cell key={col.id} className={SubframeUtils.twClassNames("min-h-[67px] py-3", cellPaddingX, alignClass, col.cellClassName)}>{content}</Table.Cell>;
          })}{hasRowActions && <DataTableRowActions row={row} actions={rowActions} cellPaddingX={cellPaddingX} ariaLabel={getRowActionLabel?.(row)} defaultOpen={rowId === initiallyOpenActionRowId} />}</Table.Row>;
      };
      $[32] = cellPaddingX;
      $[33] = columns;
      $[34] = getRowActionLabel;
      $[35] = getRowId;
      $[36] = hasRowActions;
      $[37] = initiallyOpenActionRowId;
      $[38] = onRowClick;
      $[39] = rowActions;
      $[40] = rowClassName;
      $[41] = selectedIds;
      $[42] = selectionMode;
      $[43] = toggleSelect;
      $[44] = t8;
    } else {
      t8 = $[44];
    }
    t7 = data.map(t8);
    $[18] = cellPaddingX;
    $[19] = columns;
    $[20] = data;
    $[21] = getRowActionLabel;
    $[22] = getRowId;
    $[23] = hasRowActions;
    $[24] = initiallyOpenActionRowId;
    $[25] = onRowClick;
    $[26] = rowActions;
    $[27] = rowClassName;
    $[28] = selectedIds;
    $[29] = selectionMode;
    $[30] = toggleSelect;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[45] !== t7) {
    t8 = <>{t7}</>;
    $[45] = t7;
    $[46] = t8;
  } else {
    t8 = $[46];
  }
  return t8;
}
function _temp(e) {
  return e.stopPropagation();
}